// Spiegel von backend/lib/cicd-stage-targets.js (Vertrag V.1). Änderungen immer in beiden Dateien.
const TARGET_KEY = /^(dev|qas|preprod|prod)(@[A-Za-z0-9_.-]{1,100})?$/;
const isDefaultTarget = targetId => targetId === undefined || targetId === null || targetId === "" || targetId === "default";

export const targetKeyOf = (stage, targetId) => isDefaultTarget(targetId) ? stage : `${stage}@${targetId}`;

export function parseTargetKey(key) {
  if (typeof key !== "string") return null;
  const match = TARGET_KEY.exec(key);
  return match ? { stage: match[1], targetId: match[2] ? match[2].slice(1) : "default" } : null;
}

export const isMultiTarget = byStage => Object.values(byStage || {}).some(rows => Array.isArray(rows) && rows.length > 1);

/** Erster nicht leere Wert der Kette (`values`: targetKey → Wert, `chain`: geordnete targetKeys), sonst `undefined`. */
export function inheritedValue(values, chain) {
  for (const key of chain || []) {
    const value = values?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
}

/**
 * Kettenglieder für Ziel T der Stufe S (Vertrag V.11): die Standardziele aller früheren Vorwärtsstufen, absteigend,
 * ohne Geschwister von S. Der eigene Wert von T und die Werte `Default` und Basis ergänzt der Aufrufer. Die Kette gilt
 * unabhängig von der Wizard-Auswahl. `forwardStages`: `[{ stage, targets: [{ targetId, isDefault }] }]` in Stufenreihenfolge.
 */
export function inheritanceChain(forwardStages, stage, targetId) {
  const list = Array.isArray(forwardStages) ? forwardStages : [];
  const index = list.findIndex(item => item.stage === stage);
  if (index < 0) return [];
  void targetId;
  return list.slice(0, index).reverse().map(item => {
    const targets = item.targets || [];
    const standard = targets.find(target => target.isDefault) || targets[0];
    return standard ? targetKeyOf(item.stage, standard.targetId) : null;
  }).filter(Boolean);
}

// --- Backend-Formen lesen (Integration I1) ---------------------------------------------------------------------------
// Das Backend lässt `targetId` beim Standardziel weg (Archiv, Warteschlange, Laufnachweis). Fehlt sie, ist immer das
// Standardziel gemeint; alle Leser normalisieren mit dieser Funktion.
export const DEFAULT_TARGET_ID = "default";
export const targetIdOf = value => value?.targetId ?? DEFAULT_TARGET_ID;

/**
 * Läufe eines Antragspakets je Ziel (Monitor Schema 2: `items[].targets[targetId]`; Schema 1: das Item selbst als
 * Standardziel). `order`: Ziel-IDs des Antrags (`request.targets`), sonst die Schlüssel von `item.targets`.
 * Ergebnis `[{ targetId, status, runId, runUrl, execution }]`.
 */
export function targetRunsOf(item, order) {
  if (!item) return [];
  const own = item.targets && typeof item.targets === "object" && !Array.isArray(item.targets) ? item.targets : null;
  if (!own) return [{ targetId: DEFAULT_TARGET_ID, status: item.status ?? null, runId: item.runId ?? item.execution?.runId ?? null,
    runUrl: item.runUrl ?? null, execution: item.execution ?? null }];
  const ids = Array.isArray(order) && order.length ? order.filter(id => own[id]) : Object.keys(own);
  return ids.map(targetId => ({ targetId, status: own[targetId].status ?? null, runId: own[targetId].runId ?? own[targetId].execution?.runId ?? null,
    runUrl: own[targetId].runUrl ?? null, execution: own[targetId].execution ?? null }));
}

/** Erster bekannter Lauf eines Antrags über alle Pakete und Ziele (Monitor-Link), sonst `null`. */
export function firstRunIdOf(items, order) {
  for (const item of items || []) for (const run of targetRunsOf(item, order)) if (run.runId) return run.runId;
  return null;
}
